import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Download, ExternalLink, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { fetchImportPdf } from '../../utils/api';
import { detectFileViewerType } from '../../utils/fileViewer';
import FileViewerPanel from './FileViewerPanel';
import ImportDataTabs from './ImportDataTabs';

/**
 * Determines file type from a blob path extension.
 */
const detectFileType = (path) => {
  return detectFileViewerType(path, 'pdf');
};

/**
 * Modal for reviewing an import's source file alongside its data (revenue/room stats).
 * Props:
 *   detail  — DLQ detail object (detail.state has importId, orgId, locationId, imports_nightAuditPath, etc.)
 *   locationName — display name for the hotel
 *   onClose — close handler
 */
const ImportReviewModal = ({ detail, locationName, onClose }) => {
  const state = detail.state;
  const filePath = state.imports_nightAuditPath || state.archivedBlobPath || state.sourceBlobPath;
  const fileType = detectFileType(filePath);
  const hasImportData = !!state.importId;

  const [blobUrl, setBlobUrl] = useState(null);
  const [fileLoading, setFileLoading] = useState(!!filePath);
  const [fileError, setFileError] = useState(null);
  const [fileCollapsed, setFileCollapsed] = useState(false);
  const [splitPercent, setSplitPercent] = useState(50);
  const isDragging = useRef(false);
  const splitContainerRef = useRef(null);
  const blobUrlRef = useRef(null);

  // Fetch the source file
  useEffect(() => {
    if (!filePath) { setFileLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const url = await fetchImportPdf(filePath);
        if (!cancelled) {
          setBlobUrl(url);
          blobUrlRef.current = url;
        }
      } catch (err) {
        if (!cancelled) setFileError(err.message || 'Failed to load file');
      } finally {
        if (!cancelled) setFileLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [filePath]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    };
  }, []);

  const handleDownload = () => {
    if (!blobUrl) return;
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filePath ? filePath.split('/').pop().split('?')[0] : 'file';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenInNewTab = () => {
    if (blobUrl) window.open(blobUrl, '_blank', 'noopener,noreferrer');
  };

  const modal = (
    <div
      className="fixed inset-0 bg-black/75 flex justify-center items-start"
      style={{ zIndex: 999999 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative bg-white shadow-xl flex flex-col w-[95%] h-[95%] max-w-7xl rounded-lg mt-[2.5vh] overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between px-4 py-2.5 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3 min-w-0">
            <h2 className="text-sm font-semibold text-gray-800 truncate">
              Import Review — {locationName} — {state.fileType}
              {state.effectiveDate && <span className="text-gray-500 font-normal ml-2">{state.effectiveDate.split('T')[0]}</span>}
            </h2>
          </div>
          <div className="flex items-center gap-1">
            {filePath && (
              <button
                onClick={() => setFileCollapsed(!fileCollapsed)}
                className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                title={fileCollapsed ? 'Show file viewer' : 'Hide file viewer'}
              >
                {fileCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
              </button>
            )}
            {blobUrl && (
              <>
                <button onClick={handleDownload}
                  className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded" title="Download">
                  <Download size={18} />
                </button>
                <button onClick={handleOpenInNewTab}
                  className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded" title="Open in new tab">
                  <ExternalLink size={18} />
                </button>
              </>
            )}
            <button onClick={onClose}
              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded" title="Close">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Body: resizable split view */}
        <div
          ref={splitContainerRef}
          className="flex-1 flex min-h-0"
          onMouseMove={(e) => {
            if (!isDragging.current || !splitContainerRef.current) return;
            const rect = splitContainerRef.current.getBoundingClientRect();
            const pct = ((e.clientX - rect.left) / rect.width) * 100;
            setSplitPercent(Math.max(15, Math.min(85, pct)));
          }}
          onMouseUp={() => { isDragging.current = false; }}
          onMouseLeave={() => { isDragging.current = false; }}
        >
          {/* Left: File viewer */}
          {!fileCollapsed && filePath && (
            <div style={{ width: `${splitPercent}%` }} className="border-r border-gray-200 flex flex-col min-h-0 flex-shrink-0">
              {fileLoading ? (
                <div className="flex-1 flex items-center justify-center bg-gray-100">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-600 border-t-transparent mx-auto mb-3" />
                    <p className="text-gray-600 text-sm">Loading file...</p>
                  </div>
                </div>
              ) : fileError ? (
                <div className="flex-1 flex items-center justify-center bg-gray-100">
                  <div className="text-center p-6">
                    <p className="text-red-600 font-medium mb-2">Failed to load file</p>
                    <p className="text-gray-600 text-sm">{fileError}</p>
                  </div>
                </div>
              ) : blobUrl ? (
                <FileViewerPanel url={blobUrl} fileType={fileType} className="flex-1" />
              ) : (
                <div className="flex-1 flex items-center justify-center bg-gray-100 text-gray-400 text-sm">
                  No file available
                </div>
              )}
            </div>
          )}

          {/* Drag handle */}
          {!fileCollapsed && filePath && (
            <div
              className="w-1.5 cursor-col-resize bg-gray-100 hover:bg-blue-200 active:bg-blue-300 flex-shrink-0 transition-colors"
              onMouseDown={(e) => { e.preventDefault(); isDragging.current = true; }}
            />
          )}

          {/* Right: Data tabs */}
          <div className="flex-1 flex flex-col min-h-0">
            {hasImportData ? (
              <ImportDataTabs
                stateId={state.id}
                orgId={state.orgId}
                locationId={state.locationId}
                importId={state.importId}
                dropdownZIndex={1000001}
              />
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <p className="text-sm font-medium mb-1">No normalized data</p>
                  <p className="text-xs">This file did not reach normalization stage.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default ImportReviewModal;
